import { useGetLocale, useTranslate } from '@refinedev/core';
import { Input, Radio, Select, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { learnVideoOptionsOf } from '../worksheets/model';
import { learnLanguageFor, videoTargetOf } from './model';

/**
 * E34 · a VIDEO step's Target (F-172) — "videos list the Learn videos … or take a YouTube / Vimeo link — the
 * help line reads «a YouTube or Vimeo link — CopingCard stores no video» (Q-e34-25 A)" (the Assign drawer's
 * Behavior). Shared by the Assign drawer and the pathway Builder's step editor, which both hold the target as
 * ONE string: a Learn clip's id, or the link. `videoTargetOf` (model.ts) reads the source off that string.
 *
 * The Learn list (E15's EN and RU lists are disjoint): ONE catalogue, never all (Q-cf-2, the founder 2026-09-30) —
 * the client's language (`clientLng`, `Client.lng`; on the practice board the FIRST selected client's) and, when
 * that is unknown or there is no client (the pathway Builder), the therapist's portal UI language. The clips are
 * unlabelled. Choosing another catalogue is a LATER round (the founder: "later should have a way to
 * select/customize") — no selector here yet.
 */
type Props = {
  value: string | null;
  onChange: (value: string | null, suggestedTitle?: string) => void;
  testId: string;
  /** The client's `lng` (the first selected client's); omitted / unknown: the therapist's portal language. */
  clientLng?: string | null;
};

export const VideoTargetInput = ({ value, onChange, testId, clientLng }: Props) => {
  const t = useTranslate();
  const locale = useGetLocale()();
  const language = learnLanguageFor(clientLng, locale);
  const learnOptions = useMemo(
    () => learnVideoOptionsOf(language, value).map((v) => ({ value: v.id, label: v.name, name: v.name })),
    [language, value],
  );
  const [source, setSource] = useState<'learn' | 'link'>(
    videoTargetOf(value)?.source === 'learn' ? 'learn' : 'link',
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Radio.Group
        value={source}
        onChange={(e) => {
          setSource(e.target.value);
          onChange(null);
        }}
        data-testid={`${testId}-source`}
      >
        <Radio value="link" data-testid={`${testId}-source-link`}>
          {t('homework.assign.video.link')}
        </Radio>
        <Radio value="learn" data-testid={`${testId}-source-learn`}>
          {t('homework.assign.video.learn')}
        </Radio>
      </Radio.Group>
      {source === 'learn' ? (
        <Select
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
          value={value ?? undefined}
          placeholder={t('homework.assign.video.learnPlaceholder')}
          onChange={(v: string) =>
            onChange(v, learnOptions.find((o) => o.value === v)?.name)
          }
          options={learnOptions}
          data-testid={testId}
        />
      ) : (
        <>
          <Input
            value={value ?? ''}
            placeholder={t('homework.assign.video.linkPlaceholder')}
            onChange={(e) => onChange(e.target.value || null)}
            status={value && !videoTargetOf(value) ? 'error' : undefined}
            data-testid={testId}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('homework.assign.video.linkHelp')}
          </Typography.Text>
          {value && !videoTargetOf(value) ? (
            <Typography.Text type="danger" data-testid={`${testId}-unsupported`}>
              {t('homework.assign.required.video')}
            </Typography.Text>
          ) : null}
        </>
      )}
    </div>
  );
};
